import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Sparkles, ChevronDown, Maximize2, Bot } from 'lucide-react';
import { Button } from '../ui/button';
import { sendChatMessage, getSuggestedQuestions, executeAction } from '../../api/aiChat';
import { useAuthStore } from '../../stores/authStore';
import { cn } from '../../lib/utils';
import RichMessageRenderer from './RichMessageRenderer';

/**
 * AI Chat Widget - "Flo" Assistant
 * 
 * Features:
 * - Floating bubble that expands to chat panel
 * - Rich responses with cards, tables, and action buttons
 * - AI-generated follow-up suggestions
 * - Deep linking to app pages
 * - Links to full-screen chat at /flo
 */
const AIChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();

  // Don't show widget on the full Flo page
  const isOnFloPage = location.pathname === '/flo';

  // Load suggestions on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const loadSuggestions = async () => {
      try {
        const result = await getSuggestedQuestions();
        if (result.success) {
          setSuggestions(result.questions);
        }
      } catch (error) {
        console.error('Failed to load suggestions:', error);
      }
    };
    loadSuggestions();
  }, [isAuthenticated]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Don't render if not authenticated or on Flo page - must be AFTER all hooks
  if (!isAuthenticated || isOnFloPage) {
    return null;
  }

  const handleSend = async (messageOverride = null) => {
    const messageText = messageOverride || inputValue.trim();
    if (!messageText || isLoading) return;

    setInputValue('');
    
    // Add user message to chat
    const newMessages = [
      ...messages,
      { role: 'user', content: messageText }
    ];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      // Send to API with conversation history
      const result = await sendChatMessage(messageText, messages);
      
      // Add AI response with rich data
      setMessages(prev => [
        ...prev,
        { 
          role: 'assistant', 
          content: result.response || 'I couldn\'t process that request.',
          response_type: result.response_type || 'text',
          structured_data: result.structured_data,
          action_links: result.action_links,
          follow_up_suggestions: result.follow_up_suggestions,
          toolResults: result.tool_results,
          isError: !result.success
        }
      ]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [
        ...prev,
        { 
          role: 'assistant', 
          content: 'Sorry, I encountered an error. Please try again.',
          isError: true,
          response_type: 'text'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  const handleFollowUpSelect = (followUp) => {
    handleSend(followUp);
  };

  const handleNavigation = (url) => {
    // Close the chat panel and navigate
    setIsOpen(false);
    navigate(url);
  };

  const openFullScreen = () => {
    setIsOpen(false);
    navigate('/flo');
  };

  // Handle action confirmation from AI responses
  const handleActionConfirm = async (actionType, actionParams, reason) => {
    setIsLoading(true);
    try {
      // Build reasoning object
      const reasoning = reason ? {
        id: 'custom',
        text: reason,
        custom_text: reason
      } : null;

      // Execute the action
      const result = await executeAction(actionType, actionParams, reasoning);

      if (result.success) {
        // Add success message
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: result.message || 'Action completed successfully.',
            response_type: 'text',
            action_links: result.action_links || (result.action_id ? [{
              label: 'View Created Item',
              url: `/pipeline?opportunity=${result.entity_id || result.action_id}`,
              entity_type: 'opportunity'
            }] : [])
          }
        ]);
      } else {
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: `Action failed: ${result.error || 'Unknown error'}`,
            response_type: 'text',
            isError: true
          }
        ]);
      }
    } catch (error) {
      console.error('Action execution error:', error);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error executing this action. Please try again.',
          response_type: 'text',
          isError: true
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle action cancellation
  const handleActionCancel = () => {
    setMessages(prev => [
      ...prev,
      {
        role: 'assistant',
        content: 'Action cancelled. Is there anything else I can help you with?',
        response_type: 'text'
      }
    ]);
  };

  // Handle flow completion
  const handleFlowComplete = (data) => {
    // The flow component will send the final create message
    // This callback is for any post-completion cleanup
    // Flow completion logging removed for production
  };

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 w-[420px] h-[560px] bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col z-50 animate-in slide-in-from-bottom-5 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-[#07689f] rounded-t-lg">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-white" />
              <span className="font-semibold text-white">Flo</span>
              <span className="text-xs text-blue-100 bg-[#0983bf]/50 px-1.5 py-0.5 rounded">AI</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={openFullScreen}
                className="h-8 w-8 p-0 text-white hover:bg-white/20"
                title="Open full screen"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 p-0 text-white hover:bg-white/20"
              >
                <ChevronDown className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              // Welcome state
              <div className="h-full flex flex-col items-center justify-center text-center px-4">
                <div className="w-12 h-12 rounded-full bg-[#07689f] flex items-center justify-center mb-4">
                  <Bot className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Hi, I&apos;m Flo</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Your AI assistant for AutoFlo. Ask me anything.
                </p>
                
                {/* Suggestions */}
                {suggestions.length > 0 && (
                  <div className="w-full space-y-2">
                    <p className="text-xs text-gray-400 uppercase tracking-wide">Try asking</p>
                    {suggestions.slice(0, 4).map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Messages
              <>
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      "flex",
                      msg.role === 'user' ? 'justify-end' : 'justify-start'
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[90%] rounded-lg",
                        msg.role === 'user'
                          ? 'bg-gray-900 text-white px-3 py-2 rounded-br-none'
                          : msg.isError
                          ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-none px-3 py-2'
                          : 'bg-gray-50 rounded-bl-none px-3 py-2'
                      )}
                    >
                      {msg.role === 'user' ? (
                        <div className="text-sm">{msg.content}</div>
                      ) : (
                        <RichMessageRenderer
                          message={{
                            content: msg.content,
                            response_type: msg.response_type,
                            structured_data: msg.structured_data,
                            action_links: msg.action_links,
                            follow_up_suggestions: msg.follow_up_suggestions,
                            tool_results: msg.toolResults
                          }}
                          onFollowUpSelect={handleFollowUpSelect}
                          onNavigate={handleNavigation}
                          onSendMessage={handleSend}
                          onActionConfirm={handleActionConfirm}
                          onActionCancel={handleActionCancel}
                          onFlowComplete={handleFlowComplete}
                          isLoading={isLoading}
                        />
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-lg rounded-bl-none px-4 py-3">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area */}
          <div className="p-3 border-t bg-gray-50 rounded-b-lg">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Flo anything..."
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#07689f] focus:border-transparent"
                disabled={isLoading}
              />
              <Button
                onClick={() => handleSend()}
                disabled={!inputValue.trim() || isLoading}
                size="sm"
                className="px-3 bg-[#07689f] hover:bg-[#065a8a]"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Bubble */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "fixed bottom-4 right-4 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 z-50",
          isOpen
            ? "bg-gray-600 hover:bg-gray-700"
            : "bg-[#07689f] hover:bg-[#065a8a]"
        )}
      >
        {isOpen ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <Bot className="h-6 w-6 text-white" />
        )}
      </button>
    </>
  );
};

export default AIChatWidget;
